import React, { useState } from 'react';
import { EVENT_LABELS, HIERARCHY_LABELS, eventLabel } from './analyticsUtils';

const PRESETS = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'custom', label: 'Custom' },
];

/**
 * Filter bar for the analytics page.
 *
 * Date range is either a preset or an explicit from/to pair — never both, since
 * the API gives `preset` precedence and silently ignores from/to alongside it.
 */
const AnalyticsFilters = ({ filters, options, onChange, onReset, disabled }) => {
  const [expanded, setExpanded] = useState(false);

  const set = (patch) => onChange({ ...filters, ...patch });

  const setPreset = (preset) => {
    if (preset === 'custom') {
      // Seed the custom inputs from the range the API just resolved, so switching
      // to custom does not blank the view.
      set({ preset: null, from: filters.resolvedFrom || '', to: filters.resolvedTo || '' });
    } else {
      set({ preset, from: null, to: null });
    }
  };

  const toggleInList = (key, value) => {
    const current = filters[key] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    set({ [key]: next.length ? next : null });
  };

  const isCustom = !filters.preset;

  const activeCount = [
    filters.platform, filters.appVersion, filters.eventName, filters.screen,
    filters.hierarchy, filters.supervisorId, filters.batchId, filters.search,
    filters.activity && filters.activity !== 'all' ? filters.activity : null,
    filters.minEvents, filters.minSessions,
  ].filter((v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length)).length;

  return (
    <div className="analytics-filters">
      <div className="analytics-filters-primary">
        <div className="analytics-preset-group" role="group" aria-label="Date range preset">
          {PRESETS.map((p) => {
            const active = p.id === 'custom' ? isCustom : filters.preset === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className={`analytics-preset-btn${active ? ' active' : ''}`}
                onClick={() => setPreset(p.id)}
                disabled={disabled}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {isCustom && (
          <div className="analytics-date-inputs">
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.from || ''}
              max={filters.to || undefined}
              onChange={(e) => set({ from: e.target.value || null, preset: null })}
              disabled={disabled}
              aria-label="From date"
            />
            <span className="analytics-date-sep">to</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.to || ''}
              min={filters.from || undefined}
              onChange={(e) => set({ to: e.target.value || null, preset: null })}
              disabled={disabled}
              aria-label="To date"
            />
          </div>
        )}

        <div className="analytics-filters-spacer" />

        <button
          type="button"
          className={`analytics-more-btn${expanded ? ' active' : ''}`}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <i className={`bi ${expanded ? 'bi-funnel-fill' : 'bi-funnel'}`} aria-hidden="true" />
          {' '}Filters
          {activeCount > 0 && <span className="analytics-filter-count">{activeCount}</span>}
        </button>

        {activeCount > 0 && (
          <button type="button" className="analytics-reset-btn" onClick={onReset} disabled={disabled}>
            Clear
          </button>
        )}
      </div>

      {expanded && (
        <div className="analytics-filters-expanded">
          <div className="analytics-filter-grid">
            <label className="analytics-filter-field">
              <span className="analytics-filter-label">Search mentor</span>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Name, email or username"
                value={filters.search || ''}
                onChange={(e) => set({ search: e.target.value || null })}
                disabled={disabled}
              />
            </label>

            <div className="analytics-filter-field">
              <span className="analytics-filter-label">Activity</span>
              <select
                className="form-select form-select-sm"
                value={filters.activity || 'all'}
                onChange={(e) => set({ activity: e.target.value === 'all' ? null : e.target.value })}
                disabled={disabled}
              >
                <option value="all">All mentors</option>
                <option value="active">Active only</option>
                <option value="dormant">Dormant (no activity in range)</option>
                <option value="never">Never opened the app</option>
              </select>
            </div>

            <div className="analytics-filter-field">
              <span className="analytics-filter-label">Supervisor / team</span>
              <select
                className="form-select form-select-sm"
                value={filters.supervisorId || ''}
                onChange={(e) => set({ supervisorId: e.target.value || null })}
                disabled={disabled}
              >
                <option value="">All teams</option>
                {(options.supervisors || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.hierarchy ? ` — ${HIERARCHY_LABELS[s.hierarchy] || s.hierarchy}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="analytics-filter-field">
              <span className="analytics-filter-label">Batch</span>
              <select
                className="form-select form-select-sm"
                value={filters.batchId || ''}
                onChange={(e) => set({ batchId: e.target.value || null })}
                disabled={disabled}
              >
                <option value="">All batches</option>
                {(options.batches || []).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <label className="analytics-filter-field">
              <span className="analytics-filter-label">Min events</span>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Any"
                value={filters.minEvents ?? ''}
                onChange={(e) => set({ minEvents: e.target.value === '' ? null : e.target.value })}
                disabled={disabled}
              />
            </label>

            <label className="analytics-filter-field">
              <span className="analytics-filter-label">Min sessions</span>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                placeholder="Any"
                value={filters.minSessions ?? ''}
                onChange={(e) => set({ minSessions: e.target.value === '' ? null : e.target.value })}
                disabled={disabled}
              />
            </label>
          </div>

          <FilterChips
            title="Role"
            values={options.hierarchyLevels || []}
            selected={filters.hierarchy || []}
            labelFor={(v) => HIERARCHY_LABELS[v] || v}
            onToggle={(v) => toggleInList('hierarchy', v)}
            disabled={disabled}
          />

          <FilterChips
            title="Event type"
            values={options.eventNames || Object.keys(EVENT_LABELS)}
            selected={filters.eventName || []}
            labelFor={eventLabel}
            onToggle={(v) => toggleInList('eventName', v)}
            disabled={disabled}
            note="Narrowing to non-session events removes app-duration data, so session length shows as “--”."
          />

          <FilterChips
            title="Platform"
            values={options.platforms || []}
            selected={filters.platform || []}
            labelFor={(v) => v}
            onToggle={(v) => toggleInList('platform', v)}
            disabled={disabled}
          />

          <FilterChips
            title="App version"
            values={options.appVersions || []}
            selected={filters.appVersion || []}
            labelFor={(v) => v}
            onToggle={(v) => toggleInList('appVersion', v)}
            disabled={disabled}
          />

          <div className="analytics-filter-field analytics-filter-field-wide">
            <span className="analytics-filter-label">Screen</span>
            <select
              className="form-select form-select-sm"
              value={(filters.screen && filters.screen[0]) || ''}
              onChange={(e) => set({ screen: e.target.value ? [e.target.value] : null })}
              disabled={disabled}
            >
              <option value="">All screens</option>
              {(options.screens || []).map((s) => (
                <option key={s.screen} value={s.screen}>
                  {s.screen} ({s.count.toLocaleString('en-IN')})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterChips = ({ title, values, selected, labelFor, onToggle, disabled, note }) => {
  if (!values || !values.length) return null;
  return (
    <div className="analytics-chip-group">
      <div className="analytics-chip-group-title">
        {title}
        {note ? <i className="bi bi-info-circle analytics-chip-note" title={note} aria-label={note} /> : null}
      </div>
      <div className="analytics-chip-row">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            className={`analytics-chip${selected.includes(v) ? ' active' : ''}`}
            onClick={() => onToggle(v)}
            disabled={disabled}
            aria-pressed={selected.includes(v)}
          >
            {labelFor(v)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default AnalyticsFilters;
